// Builder option explainer — a clickable "ⓘ" that opens a clean panel describing
// any choice (race, class, background, alignment, ability, skill, feat, subclass,
// spell) with its description + salient mechanics. Data comes from /api/srd/detail,
// which reads each entry's own data — nothing hardcoded here.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { OptionDetail } from "../lib/types";
import { titleizeSlug } from "../lib/format";

export function OptionInfo({ kind, index, label }: { kind: string; index: string; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="opt-info-btn"
        title={`About ${label || index}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >ⓘ</button>
      {open && <OptionDetailModal kind={kind} index={index} onClose={() => setOpen(false)} />}
    </>
  );
}

export function OptionDetailModal({ kind, index, onClose }: { kind: string; index: string; onClose: () => void }) {
  const [detail, setDetail] = useState<OptionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.detail(kind, index)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : "Couldn't load details"); });
    return () => { alive = false; };
  }, [kind, index]);

  return (
    <div className="opt-modal-backdrop" onClick={onClose}>
      <div className="opt-modal" onClick={(e) => e.stopPropagation()}>
        <button className="opt-modal__x" onClick={onClose}>✕</button>
        {!detail && !error && <div className="muted">Loading…</div>}
        {error && <div className="auth-error">{error}</div>}
        {detail && <OptionDetailBody d={detail} />}
      </div>
    </div>
  );
}

export function OptionDetailBody({ d }: { d: OptionDetail }) {
  const chips: { k: string; v: string }[] = [];
  if (d.kind === "spells") {
    if (d.level !== undefined) chips.push({ k: "Level", v: d.level === 0 ? "Cantrip" : String(d.level) });
    if (d.school) chips.push({ k: "School", v: d.school });
    if (d.casting_time) chips.push({ k: "Cast", v: d.casting_time });
    if (d.range) chips.push({ k: "Range", v: d.range });
    if (d.duration) chips.push({ k: "Duration", v: d.duration });
    if (d.concentration) chips.push({ k: "", v: "Concentration" });
    if (d.ritual) chips.push({ k: "", v: "Ritual" });
  }
  if (d.kind === "races") {
    if (d.size) chips.push({ k: "Size", v: d.size });
    if (d.speed !== undefined) chips.push({ k: "Speed", v: `${d.speed} ft` });
    (d.ability_bonuses || []).forEach((b) =>
      chips.push({ k: "", v: `+${b.bonus} ${b.ability?.toUpperCase()}` }));
  }
  if (d.kind === "classes") {
    if (d.hit_die) chips.push({ k: "Hit Die", v: `d${d.hit_die}` });
    if (d.spellcasting) chips.push({ k: "", v: "Spellcaster" });
    (d.saving_throws || []).forEach((s) => chips.push({ k: "Save", v: s }));
  }
  if (d.kind === "skills" && d.ability) chips.push({ k: "Ability", v: d.ability });
  if (d.kind === "ability-scores" && d.full_name) chips.push({ k: "", v: d.full_name });

  return (
    <div className="opt-detail">
      <div className="opt-detail__title">{titleizeSlug(d.name || d.index)}</div>
      {chips.length > 0 && (
        <div className="opt-detail__chips">
          {chips.map((c, i) => (
            <span key={i} className="opt-chip">{c.k && <b>{c.k} </b>}{c.v}</span>
          ))}
        </div>
      )}
      {d.desc && <Para text={d.desc} />}
      {d.kind === "spells" && d.higher_level && (
        <div className="opt-detail__sub"><div className="opt-detail__sublabel">At Higher Levels</div><Para text={d.higher_level} /></div>
      )}
      {d.kind === "races" && (d.traits?.length ?? 0) > 0 && (
        <div className="opt-detail__sub">
          <div className="opt-detail__sublabel">Traits</div>
          {d.traits!.map((t, i) => (
            <div key={i} className="opt-trait">
              <span className="opt-trait__name">{t.name}{t.real === false ? " · narrative" : t.real ? " · auto" : ""}</span>
              <span className="opt-trait__desc">{t.desc}</span>
            </div>
          ))}
          {d.languages?.length ? <div className="opt-detail__meta">Languages: {d.languages.join(", ")}</div> : null}
        </div>
      )}
      {d.kind === "classes" && (d.proficiencies?.length ?? 0) > 0 && (
        <div className="opt-detail__meta">Proficiencies: {d.proficiencies!.join(", ")}</div>
      )}
      {d.kind === "subclasses" && (d.features?.length ?? 0) > 0 && (
        <div className="opt-detail__sub">
          <div className="opt-detail__sublabel">Features by Level</div>
          {d.features!.slice().sort((a, b) => a.level - b.level).map((f, i) => (
            <div key={i} className="opt-trait">
              <span className="opt-trait__name">L{f.level} · {f.name}{f.real === false ? " · narrative" : f.real ? " · auto" : ""}</span>
              <span className="opt-trait__desc">{f.desc}</span>
            </div>
          ))}
        </div>
      )}
      {d.kind === "backgrounds" && (
        <div className="opt-detail__meta">
          {d.starting_proficiencies?.length ? <div>Grants: {d.starting_proficiencies.join(", ")}</div> : null}
          {d.feat ? <div>Origin feat: {titleizeSlug(d.feat)}</div> : null}
        </div>
      )}
    </div>
  );
}

function Para({ text }: { text: string }) {
  return (
    <div className="opt-detail__desc">
      {text.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}
